/**
 * Guest listing inquiry calendar — local date strings and stay-range validation.
 * Check-out day is exclusive (matches backend inquiry night semantics).
 */

export function getLocalDateString(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function normalizeBlockedDateSet(blockedDates = []) {
  const blockedDateSet = new Set();
  for (const entry of blockedDates) {
    const raw = typeof entry === 'string' ? entry : entry?.date;
    const normalized = String(raw || '').trim().split('T')[0];
    if (normalized) {
      blockedDateSet.add(normalized);
    }
  }
  return blockedDateSet;
}

export function atLocalMidnight(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addLocalDays(date, days) {
  const next = atLocalMidnight(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function isCalendarDateUnavailable(date, blockedDateSet) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return false;
  }
  return blockedDateSet.has(getLocalDateString(date));
}

/**
 * Validate occupied stay nights for [checkIn, checkOut) — checkout day excluded.
 */
export function stayRangeIncludesUnavailableDate(checkIn, checkOut, blockedDateSet) {
  if (!(checkIn instanceof Date) || !(checkOut instanceof Date)) {
    return { valid: false, reason: 'invalid' };
  }

  const start = atLocalMidnight(checkIn);
  const end = atLocalMidnight(checkOut);

  if (end <= start) {
    return { valid: false, reason: 'invalid' };
  }

  let current = start;
  while (current < end) {
    const dateStr = getLocalDateString(current);
    if (blockedDateSet.has(dateStr)) {
      return { valid: false, reason: 'unavailable', blockedDate: dateStr };
    }
    current = addLocalDays(current, 1);
  }

  return { valid: true, reason: '' };
}

export function resolveCheckoutSelection({ checkIn, checkOut, blockedDateSet }) {
  if (!checkIn || !checkOut) {
    return {
      accepted: false,
      errorKey: 'invalid',
      selecting: 'checkOut',
      selectedDates: { checkIn, checkOut: null },
    };
  }

  if (checkOut <= checkIn) {
    return {
      accepted: false,
      errorKey: 'invalid',
      selecting: 'checkOut',
      selectedDates: { checkIn: checkOut, checkOut: null },
    };
  }

  const validation = stayRangeIncludesUnavailableDate(checkIn, checkOut, blockedDateSet);
  if (!validation.valid) {
    return {
      accepted: false,
      errorKey: validation.reason === 'unavailable' ? 'unavailable' : 'invalid',
      selecting: 'checkOut',
      selectedDates: { checkIn, checkOut: null },
    };
  }

  return {
    accepted: true,
    errorKey: '',
    selecting: 'checkIn',
    selectedDates: { checkIn, checkOut },
  };
}

export function createClearedDateSelection() {
  return {
    selectedDates: { checkIn: null, checkOut: null },
    selecting: 'checkIn',
    rangeErrorKey: '',
  };
}

/**
 * Sample preview calendar data — deterministic demo-only fixture.
 * Does not call the backend and does not scatter blocked dates across navigated months.
 */
export function getSamplePreviewDemoBlockedDates(referenceDate = new Date()) {
  const anchor = addLocalDays(atLocalMidnight(referenceDate), 30);
  return [
    { date: getLocalDateString(anchor), reason: 'demo' },
    { date: getLocalDateString(addLocalDays(anchor, 1)), reason: 'demo' },
  ];
}

export function getSamplePreviewCalendarData({
  referenceDate = new Date(),
  includeDemoBlocks = false,
} = {}) {
  return {
    blocked_dates: includeDemoBlocks ? getSamplePreviewDemoBlockedDates(referenceDate) : [],
    sample_preview: true,
    demo_only: true,
    calendar_start: getLocalDateString(referenceDate),
  };
}
